import { useState } from "react";
import { Send, Copy, Mail, MessageCircle, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { fdate, run, useLoad } from "../lib/util";
import { waLink } from "../lib/whatsapp";
import { Alert, Badge, Btn, Field, Input, Select, Table, Empty } from "../components/kit";

const TONE = { pendente: "amber", aceito: "green", cancelado: "muted" };

function Share({ invite, school, onClose }) {
  const link = `${window.location.origin}/convite/${invite.token}`;
  const text = `Olá, ${invite.name}! Você foi convidado(a) para acessar o MapIA da escola ${school?.name || ""}. Crie sua senha pelo link (válido até ${fdate(invite.expires_at)}): ${link}`;
  const wa = waLink(school?.whatsapp, text);
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copiado"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-share-dialog">
        <DialogHeader><DialogTitle>Enviar convite para {invite.email}</DialogTitle></DialogHeader>
        <Input readOnly data-testid="invite-link-input" value={link} onFocus={(e) => e.target.select()} />
        <p className="text-xs text-slate-500">Por segurança, este link só é exibido agora. Se perder, use “Gerar novo link”. O envio automático por e-mail não está integrado: escolha um meio abaixo.</p>
        <div className="flex flex-wrap gap-2">
          <Btn variant="outline" data-testid="invite-copy-button" onClick={copy}><Copy className="w-4 h-4" />Copiar link</Btn>
          <a href={`mailto:${invite.email}?subject=${encodeURIComponent("Convite para o MapIA")}&body=${encodeURIComponent(text)}`}><Btn variant="outline" data-testid="invite-email-button"><Mail className="w-4 h-4" />Abrir e-mail</Btn></a>
          {wa ? <a href={wa} target="_blank" rel="noreferrer"><Btn data-testid="invite-whatsapp-button"><MessageCircle className="w-4 h-4" />Enviar por WhatsApp</Btn></a> : <Badge tone="amber">Escola sem WhatsApp cadastrado</Badge>}
        </div>
        <DialogFooter><Btn variant="navy" onClick={onClose}>Concluir</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Invites({ schools }) {
  const [list, reload] = useLoad(async () => (await api.get("/admin/invites")).data, []);
  const [offer, reloadOffer] = useLoad(async () => (await api.get("/public/offer")).data, []);
  const [f, setF] = useState({ name: "", email: "", school_id: "", offer: true });
  const [share, setShare] = useState(null);
  const school = (id) => schools.find((s) => s.id === id);
  const create = async () => {
    const r = await run(() => api.post("/admin/invites", { ...f, offer: f.offer && !!offer?.active }), "Convite criado");
    if (r) { setShare(r.data); setF({ name: "", email: "", school_id: "", offer: true }); reload(); reloadOffer(); }
  };
  const renew = async (i) => {
    const r = await run(() => api.post(`/admin/invites/${i.id}/renew`), "Novo link gerado (o anterior deixou de valer)");
    if (r) { setShare(r.data); reload(); }
  };
  const cancel = async (i) => window.confirm("Cancelar este convite?") && (await run(() => api.delete(`/admin/invites/${i.id}`), "Convite cancelado")) && reload();
  return (
    <div className="space-y-3 pt-6">
      <h3 className="font-heading font-bold text-lg text-[#0B192C]">Convidar novo usuário</h3>
      <Alert tone="info">O convidado recebe um link (válido por 7 dias) para criar a própria senha e entra como gestor da escola escolhida.</Alert>
      <div className="grid sm:grid-cols-4 gap-3 items-end bg-white border rounded-xl p-4">
        <Field label="Nome"><Input data-testid="invite-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="E-mail"><Input data-testid="invite-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Escola"><Select data-testid="invite-school-select" value={f.school_id} onChange={(e) => setF({ ...f, school_id: e.target.value })} placeholder="Selecione" options={schools.map((s) => [s.id, s.name])} /></Field>
        <Btn data-testid="invite-create-button" onClick={create}><Send className="w-4 h-4" />Criar convite</Btn>
        <label className="sm:col-span-4 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="invite-offer-checkbox" disabled={!offer?.active} checked={f.offer && !!offer?.active} onChange={(e) => setF({ ...f, offer: e.target.checked })} />
          {offer?.active ? <>Usar uma vaga da oferta de lançamento (primeiro mapa grátis) — restam <b data-testid="invite-offer-remaining">{offer.remaining}</b> de {offer.total}</> : "As 100 vagas da oferta de lançamento já foram usadas"}
        </label>
      </div>
      <Table testid="invites-table">
        <thead><tr><th>Nome</th><th>E-mail</th><th>Escola</th><th>Situação</th><th>Validade</th><th /></tr></thead>
        <tbody>
          {(list || []).map((i) => (
            <tr key={i.id}>
              <td>{i.name}</td><td>{i.email}</td><td>{school(i.school_id)?.name || "—"}</td>
              <td><Badge tone={TONE[i.status]}>{i.status}</Badge></td>
              <td>{i.status === "pendente" ? fdate(i.expires_at) : fdate(i.accepted_at)}</td>
              <td className="whitespace-nowrap">
                {i.status !== "aceito" && <Btn variant="ghost" data-testid={`invite-renew-${i.email}`} onClick={() => renew(i)}><RefreshCw className="w-4 h-4" />Gerar novo link</Btn>}
                {i.status === "pendente" && <Btn variant="ghost" onClick={() => cancel(i)}><X className="w-4 h-4 text-red-600" /></Btn>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {list && !list.length && <Empty>Nenhum convite enviado.</Empty>}
      {share && <Share invite={share} school={school(share.school_id)} onClose={() => setShare(null)} />}
    </div>
  );
}
